<template>
  <b-container>
    <WizardPageTitle :title="`Details of ${currentModel.name}`" />
    <b-row>
      <b-col>
        <b-form>
          <b-form-group
            label="Model name"
            description="Model name, must be a valid JavaScript identifier in TitleCase format"
            :state="errState($v.name)"
            invalid-feedback="A valid model name is required"
          >
            <b-form-input
              ref="name"
              v-model="$v.name.$model"
              type="text"
              :state="errState($v.name)"
            />
          </b-form-group>
          <b-form-group
            description="Enable REST API for creating/reading/updating/deleting
              (CRUD) operations on this model"
          >
            <b-form-checkbox v-model="enableApi">
              Enable API at
              <code>/api/v1/{{ apiPrefix }}/</code>
            </b-form-checkbox>
          </b-form-group>
        </b-form>
      </b-col>
    </b-row>
    <b-row class="mt-2">
      <b-col>
        <h4 class="border-bottom">
          Fields
        </h4>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="2">
        <b-nav
          vertical
          pills
          class="pr-1"
        >
          <b-nav-item
            key="id"
            to="#"
            disabled
          >
            {{ idFieldName }}
          </b-nav-item>
          <b-nav-item
            v-for="field in currentModel.fields"
            :key="field.name"
            to="#"
            :active="currentField === field"
            :disabled="field.name === '_id'"
            :class="field.fieldError ? 'nav-link-danger' : ''"
            @click.prevent="selectField(field)"
          >
            {{ field.name }}
          </b-nav-item>
          <b-nav-item>
            <b-button
              variant="outline-primary"
              size="sm"
              @click="addField"
            >
              <b-icon icon="plus" />add field&nbsp;
            </b-button>
          </b-nav-item>
        </b-nav>
      </b-col>
      <b-col v-if="currentField">
        <FieldDetail
          :key="currentField.name"
          :field="currentField"
          :all-fields="currentModel.fields"
          @update="onFieldUpdate"
          @error="onCurrentFieldError"
          @remove="onFieldRemove"
        />
      </b-col>
      <b-col
        v-else
        class="text-center font-italic"
      >
        <div>
          Select field to edit or
          <a
            href="#"
            @click.prevent="addField"
          >add a new field</a>.
        </div>
      </b-col>
    </b-row>
    <b-row class="border-top pt-2">
      <b-col class="text-right">
        <b-button
          variant="primary"
          :disabled="error"
          @click="onSaveChanges"
        >
          Save model changes
        </b-button>
      </b-col>
    </b-row>
  </b-container>
</template>

<style scoped lang="scss">
  ul.nav {
    border-right: 1px solid rgba(0, 0, 0, 0.125);
  }

  .nav-link-danger {
    background-color: #f5c6cb;
    border-radius: 0.25rem;

    & a {
      color: #721c24;

      &.active {
        color: #fff;
        background-color: #721c24;
        border-color: #721c24;
      }
    }
  }
</style>

<script>
import { mapState } from 'vuex';
import log from 'loglevel';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';

import WizardPageTitle from './widgets/WizardPageTitle.vue';
import FieldDetail from './FieldDetail.vue';
import navigationMixin from './mixins/navigation';

const any = (array) => array.reduce((acc, val) => acc || val, false);

const getCurrentModel = (currentStep, models) => {
  const name = currentStep.split('/')[1];
  for (const m of models) {
    if (m.name === name) return m;
  }
  log.error(`getCurrentModel(): no model matching current step ${currentStep}`);
  return null;
};

const generateFieldName = (fieldNames) => {
  for (let seq = fieldNames.length + 1; seq < 100; seq += 1) {
    const name = `field${seq}`;
    if (!fieldNames.includes(name)) return name;
  }
  // fallback if we've got over 100 fields defined (or more probably, a bug in our code)
  return 'newField';
};

const mustBeTitleCase = (value) => !!value.match(/^[A-Z][A-Za-z0-9]+$/);

export default {
  name: 'ModelDetail',
  components: {
    WizardPageTitle,
    FieldDetail,
  },
  mixins: [validationMixin, navigationMixin],
  data() {
    const m = getCurrentModel(this.$store.state.currentStep, this.$store.state.recipe.models);

    return {
      originalName: m ? m.name : null,
      name: m ? m.name : '',
      fields: m ? m.fields : [],
      enableApi: m ? m.enable_api : false,
      currentField: null,
    };
  },
  validations: {
    name: {
      required,
      mustBeTitleCase,
    },
  },
  computed: {
    ...mapState(['currentStep', 'recipe', 'error']),
    currentModel() {
      return getCurrentModel(this.currentStep, this.recipe.models);
    },
    apiPrefix() {
      // this needs to correspond to backend slug format generation
      return this.name.replace(/[A-Z]+/g, (m) => `-${m.toLowerCase()}`).replace(/^-+/, '');
    },
    idFieldName() {
      return this.recipe.orm === 'mongoose' ? '_id' : 'id';
    },
  },
  watch: {
    '$v.$invalid': function watchError() {
      this.setError();
    },
  },
  async mounted() {
    await this.$nextTick();
    this.$refs.name.focus();
  },
  methods: {
    selectField(field) {
      if (field.name === '_id') return;
      if (this.currentField && this.currentField.fieldError) return;
      this.currentField = field;
    },
    addField() {
      const name = generateFieldName(this.fields.map((f) => f.name));
      const field = {
        name,
        type: 'string',
      };
      this.fields.push(field);
      this.selectField(field);
    },
    onCurrentFieldError(isError) {
      this.$set(this.currentField, 'fieldError', isError);
      this.setError();
    },
    onFieldUpdate({ field, data }) {
      // By the time we're called, currentField might've been changed
      // so we use `field` to reference the actual field that was used
      // by the FieldDetail component

      for (const prop in data) { // eslint-disable-line
        field[prop] = data[prop]; // eslint-disable-line
      }
    },
    onFieldRemove(field) {
      const idx = this.fields.indexOf(field);
      if (idx === -1) {
        log.error(`onFieldRemove(): asked to remove field ${field.name} that's not in fields`);
        return;
      }
      this.currentField = null;
      this.$delete(this.fields, idx);
    },
    errState(validation) {
      return validation.$error ? false : null;
    },
    setError() {
      const anyFieldError = any(this.fields.map((f) => f.fieldError));

      if (this.$v.$invalid || anyFieldError) {
        this.$store.commit('setError', true);
      } else {
        this.$store.commit('clearError');
      }
    },
    onSaveChanges() {
      this.$eventHub.$emit('navigate-attempt', 'models');
    },
    commit() {
      if (this.$v.$invalid) return false;

      this.$store.commit('setModel', {
        name: this.name,
        fields: this.fields,
        enable_api: this.enableApi,
        originalName: this.originalName,
      });

      return true;
    },
  },
};
</script>
